import React, { useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { Play, Pause, Maximize, Minimize, Radio, AlertCircle, Loader2, SkipBack, SkipForward } from 'lucide-react';
import { StreamState, SeekOption } from '../types';
import { Button } from './Button';

/** Format the seek option into a human-readable timestamp */
function formatSeekTime(seekOption: SeekOption): string {
  const match = seekOption.match(/^-(\d+)h$/);
  if (!match) return seekOption;

  const hoursAgo = parseInt(match[1], 10);
  const targetTime = new Date(Date.now() - hoursAgo * 60 * 60 * 1000);

  // Format as "Jan 2, 4:30 PM"
  return targetTime.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

interface VideoPlayerProps {
  streamState: StreamState;
  streamingUrl: string | null;
  isLoading: boolean;
  error: string | null;
  togglePlay: () => void;
  goLive: () => void;
  onRetry: () => void;
  onSkipBack?: () => void;
  onSkipForward?: () => void;
}

const RUTABAGA_PLACEHOLDER_IMG = "/rutabaga.png";

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  streamState,
  streamingUrl,
  isLoading,
  error,
  togglePlay,
  goLive,
  onRetry,
  onSkipBack,
  onSkipForward,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Also handle iOS webkit fullscreen events on video element
    const video = videoRef.current;
    const handleWebkitFullscreenChange = () => {
      // Check if video is in webkit fullscreen mode
      const videoElement = video as HTMLVideoElement & { webkitDisplayingFullscreen?: boolean };
      setIsFullscreen(!!videoElement?.webkitDisplayingFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    video?.addEventListener('webkitbeginfullscreen', () => setIsFullscreen(true));
    video?.addEventListener('webkitendfullscreen', () => setIsFullscreen(false));

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      video?.removeEventListener('webkitbeginfullscreen', () => setIsFullscreen(true));
      video?.removeEventListener('webkitendfullscreen', () => setIsFullscreen(false));
    };
  }, []);

  const toggleFullscreen = () => {
    const video = videoRef.current as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
      webkitExitFullscreen?: () => void;
      webkitSupportsFullscreen?: boolean;
    };

    // iOS Safari: use video element's native fullscreen
    if (video?.webkitSupportsFullscreen && video.webkitEnterFullscreen) {
      if (!isFullscreen) {
        video.webkitEnterFullscreen();
      }
      return;
    }

    // Standard Fullscreen API
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const initializeHls = useCallback((url: string) => {
    if (!videoRef.current) return;

    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
      });

      hlsRef.current = hls;

      hls.loadSource(url);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed, ready for playback');
        if (streamState.isPlaying) {
          videoRef.current?.play().catch(err => {
            console.log('Autoplay prevented:', err);
          });
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Network error occurred while loading video');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Media error occurred while playing video');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error occurred while playing video');
              break;
          }
        }
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      videoRef.current.src = url;
      videoRef.current.addEventListener('loadedmetadata', () => {
        if (streamState.isPlaying) {
          videoRef.current?.play().catch(err => {
            console.log('Autoplay prevented:', err);
          });
        }
      });
    }
  }, [streamState.isPlaying]);

  // Initialize HLS when streaming URL changes
  useEffect(() => {
    if (streamingUrl) {
      initializeHls(streamingUrl);
    }
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamingUrl, initializeHls]);

  // Handle play/pause
  useEffect(() => {
    if (!videoRef.current) return;
    if (streamState.isPlaying) {
      videoRef.current.play().catch(console.log);
    } else {
      videoRef.current.pause();
    }
  }, [streamState.isPlaying]);


  const hasStream = !!streamingUrl && !isLoading && !error;

  return (
    <div className="w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 p-2 sm:p-4">
      {/* Video Container */}
      <div
        ref={containerRef}
        className="relative aspect-video bg-zinc-900 rounded-2xl overflow-hidden group"
      >
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-700 via-gray-900 to-black opacity-80"></div>

        {/* Video Element (hidden when no stream) */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-contain z-10 ${hasStream ? 'opacity-100' : 'opacity-0'}`}
          playsInline
          muted
        />

        {/* Placeholder Image (shown when no stream) */}
        {!hasStream && !isLoading && !error && (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center p-4 z-10">
            <img
              src={RUTABAGA_PLACEHOLDER_IMG}
              alt="Baga Stream Placeholder"
              className="h-full w-full object-contain transition-opacity duration-500 opacity-60"
            />
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-30 bg-black/60">
            <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
            <p className="text-white text-sm">Loading stream...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-30 bg-black/60 text-white px-4">
            <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
            <h3 className="text-lg font-heading font-bold mb-2">Stream Unavailable</h3>
            <p className="text-sm opacity-80 text-center mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={onRetry} className="border-white text-white hover:bg-white hover:text-black">
              Try Again
            </Button>
          </div>
        )}

        {/* Live Badge */}
        <div className="absolute top-4 left-4 z-20">
          <div className={`
            flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md transition-colors duration-300
            ${streamState.isLive && hasStream ? 'bg-rutabaga-green/90 text-white' : 'bg-gray-800/80 text-gray-400'}
          `}>
            <div className={`w-2.5 h-2.5 rounded-full ${streamState.isLive && hasStream ? 'bg-white animate-pulse' : 'bg-gray-500'}`} />
            <span className="text-xs font-bold tracking-wider uppercase">
              {streamState.isLive && hasStream ? 'LIVE' : isLoading ? 'CONNECTING' : 'OFFLINE'}
            </span>
          </div>
        </div>

        {/* Center Play Button Overlay (only show when stream is available but paused) */}
        {hasStream && !streamState.isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/40 backdrop-blur-[2px]">
            <button
              onClick={togglePlay}
              className="transform hover:scale-110 transition-all duration-200 group-hover:shadow-2xl"
            >
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-rutabaga-wood rounded-full flex items-center justify-center shadow-lg border-4 border-white/30">
                <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-2" fill="currentColor" />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

        {/* Left Controls */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Skip Back 15s */}
          <button
            onClick={onSkipBack}
            disabled={!hasStream || !onSkipBack}
            className="p-2 rounded-full hover:bg-rutabaga-bg text-rutabaga-text transition-colors focus:outline-none focus:ring-2 focus:ring-rutabaga-wood disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Skip back 15 seconds"
            title="Skip back 15 seconds"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          <button
            onClick={togglePlay}
            disabled={!hasStream}
            className="p-2.5 rounded-full hover:bg-rutabaga-bg text-rutabaga-text transition-colors focus:outline-none focus:ring-2 focus:ring-rutabaga-wood disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={streamState.isPlaying ? "Pause" : "Play"}
          >
            {streamState.isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" fill="currentColor" />}
          </button>

          {/* Skip Forward 15s */}
          <button
            onClick={onSkipForward}
            disabled={!hasStream || !onSkipForward || streamState.isLive}
            className="p-2 rounded-full hover:bg-rutabaga-bg text-rutabaga-text transition-colors focus:outline-none focus:ring-2 focus:ring-rutabaga-wood disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Skip forward 15 seconds"
            title="Skip forward 15 seconds"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          <div className="h-4 w-[1px] bg-gray-300 mx-2"></div>

          <span className="text-xs font-mono text-gray-500 font-medium">
            {hasStream && streamState.isLive
              ? 'LIVE'
              : streamState.currentSeek
                ? formatSeekTime(streamState.currentSeek)
                : '--:--:--'}
          </span>
        </div>

        {/* Right Controls */}
        <div className="flex items-center justify-between w-full sm:w-auto gap-4">

          {/* Jump to Live Button */}
          {hasStream && !streamState.isLive && (
             <Button
                variant="secondary"
                size="sm"
                onClick={goLive}
                className="animate-in fade-in zoom-in duration-300"
             >
                <Radio className="w-4 h-4 mr-2" />
                JUMP TO LIVE
             </Button>
          )}

          <button
            onClick={toggleFullscreen}
            className="p-2.5 rounded-full hover:bg-rutabaga-bg text-rutabaga-text transition-colors ml-auto sm:ml-0 focus:outline-none focus:ring-2 focus:ring-rutabaga-wood"
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};